<template>
  <DataTable
    :value="rows"
    :frozenValue="editMulti"
    :scrollHeight="$isLargeScreen.value ? '500px' : undefined"
    :scrollable="$isLargeScreen.value"
    v-model:selection="selection"
    responsiveLayout="stack"
    dataKey="id"
  >
    <Column
      header="Brinco"
      field="output.earring"
      style="min-width: 140px"
      alignFrozen="left"
      :frozen="true"
      :sortable="true"
    >
      <template #header>
        <Checkbox
          :binary="true"
          class="p-mr-4"
          :modelValue="selectionAll"
          @change="onChangeSelectionAll()"
        ></Checkbox>
      </template>
      <template #body="{ data }">
        <Checkbox
          :binary="true"
          class="p-mr-4"
          :modelValue="selectionAll || selection[data.id]"
          @change="onChangeSelection(data)"
        ></Checkbox>
        <InputNumber
          v-if="data.editing && !data.editMulti"
          v-model="data.output.earring"
        />
        <span
          v-else
          :class="{ existent: !!data.output.id }"
          v-tooltip.right="data.output.id ? 'Bovino já cadastrado' : ''"
        >
          <router-link
            v-if="data.output.id"
            :to="{ name: 'bovine.edit', params: { id: data.output.id } }"
            target="_blank"
            style="text-decoration: none"
          >
            <strong>{{ data.output.earring }}</strong>
          </router-link>
          <span v-else-if="!data.editMulti">
            {{ data.output.earring || '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Tipo"
      field="type.name"
      style="min-width: 120px"
      alignFrozen="left"
      :frozen="true"
      :sortable="true"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutocompleteApi
            v-model:value="data.output.type"
            v-model:valueId="data.output.typeId"
            :search="fetchTypes"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span v-if="data.input.type && !data.output.type" class="invalid">
            {{ data.input.type }}
          </span>
          <BovineType v-else :type="data.output.type" small />
        </span>
      </template>
    </Column>
    <Column header="Raça" :sortable="true" style="min-width: 120px">
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutocompleteApi
            v-model:value="data.output.breed"
            v-model:valueId="data.output.breedId"
            :search="fetchBreeds"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span v-if="data.input.breed && !data.output.breed" class="invalid">
            {{ data.input.breed }}
          </span>
          <span v-else>
            {{ data.output.breed?.name || '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Data de nascimento"
      field="birthDate"
      :sortable="true"
      style="min-width: 150px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <Calendar
            v-model="data.output.birthDate"
            dateFormat="dd/mm/yy"
            showIcon
            :monthNavigator="true"
            :yearNavigator="true"
            :yearRange="birthDateRange"
            :showOnFocus="false"
            v-mask="'##/##/####'"
          />
        </span>
        <span v-else>
          <span
            v-if="data.input.birth && !data.output.birthDate"
            class="invalid"
          >
            {{ data.input.birth }}
          </span>
          <span v-else>
            {{ $formatDate(data.output.birthDate) || '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Previsão de parto"
      field="childBirth"
      :sortable="true"
      style="min-width: 150px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <Calendar
            v-model="data.output.childBirth"
            dateFormat="dd/mm/yy"
            showIcon
            :monthNavigator="true"
            :yearNavigator="true"
            :yearRange="birthDateRange"
            :showOnFocus="false"
            v-mask="'##/##/####'"
          />
        </span>
        <span v-else>
          <span
            v-if="data.input.childBirth && !data.output.childBirth"
            class="invalid"
          >
            {{ data.input.childBirth }}
          </span>
          <span v-else>
            {{ $formatDate(data.output.childBirth) || '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Peso"
      field="output.weight"
      :sortable="true"
      style="min-width: 100px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <InputNumber
            v-model="data.output.weight"
            mode="decimal"
            :minFractionDigits="2"
          />
        </span>
        <span v-else>
          <span v-if="data.input.weight && !data.output.weight" class="invalid">
            {{ data.input.weight }}
          </span>
          <span v-else>
            {{ data.output.weight ? $toDecimal(data.output.weight) : '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Status"
      field="output.status.name"
      :sortable="true"
      style="min-width: 120px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutocompleteApi
            v-model:value="data.output.status"
            v-model:valueId="data.output.statusId"
            :search="fetchStatus"
            @change="onStatusChange(data, $event)"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span v-if="data.input.status && !data.output.status" class="invalid">
            {{ data.input.status }}
          </span>
          <span v-else>
            {{ data.output.status?.name || '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      v-for="i in serviceId ? 0 : 1"
      :key="i"
      header="Touro"
      field="output.bullType.name"
      :sortable="true"
      style="min-width: 120px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutoComplete
            v-model="data.output.bullType"
            :suggestions="bullTypes"
            @complete="fetchBullTypes($event)"
            field="name"
            dropdown
          ></AutoComplete>
        </span>
        <span v-else>
          {{ data.output.bullType?.name || data.output?.bullType || '-' }}
        </span>
      </template>
    </Column>
    <Column header="Matriz" :sortable="true" style="min-width: 90px">
      <template #body="{ data }">
        <span v-if="data.editing && !data.editMulti">
          <AutocompleteApi
            v-model:value="data.output.bovineRoot"
            v-model:valueId="data.output.bovineRootId"
            :labelField="'earring'"
            :search="(query) => fetchBovineRoot(data.output, query)"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span
            v-if="
              data.input.root &&
              !data.output.bovineRoot &&
              !data.output.bovineRootEarring
            "
            class="invalid"
          >
            {{ data.input.root }}
          </span>
          <span v-else>
            <span v-if="data.output.bovineRoot?.id">
              <router-link
                :to="{
                  name: 'bovine.edit',
                  params: { id: data.output.bovineRoot?.id },
                }"
                target="_blank"
                style="text-decoration: none"
              >
                {{ data.output.bovineRoot?.earring }}
              </router-link>
            </span>
            <span v-else-if="data.output.bovineRootEarring">
              {{ data.output.bovineRootEarring }}
            </span>
            <span v-else-if="!data.editMulti"> - </span>
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Lote/Piquete"
      field="output.lotId"
      :sortable="true"
      style="min-width: 130px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutocompleteApi
            v-model:value="data.output.farmLot"
            v-model:valueId="data.output.lotId"
            :search="fetchFarmLots"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span
            v-if="data.input.farmLot && !data.output.farmLot"
            class="invalid"
          >
            {{ data.input.farmLot }}
          </span>
          <span v-else>
            <span v-if="data.output.farmLot?.id">
              <router-link
                :to="{
                  name: 'lot.edit',
                  params: { id: data.output.farmLot?.id },
                }"
                target="_blank"
                style="text-decoration: none"
              >
                {{ data.output.farmLot?.name }}
              </router-link>
            </span>
            <span v-else> - </span>
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Fazenda"
      field="output.farmId"
      style="min-width: 130px"
      :sortable="true"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutocompleteApi
            v-model:value="data.output.farm"
            v-model:valueId="data.output.farmId"
            :search="fetchFarms"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span v-if="data.input.farm && !data.output.farm" class="invalid">
            {{ data.input.farm }}
          </span>
          <span v-else>
            <router-link
              v-if="data.output.farm?.name"
              :to="{ name: 'farm.edit', params: { id: data.output.farm?.id } }"
              target="_blank"
              style="text-decoration: none"
              >{{ data.output.farm?.name }}</router-link
            >
            <span v-else> - </span>
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Proprietário"
      field="ouput.ownerId"
      :sortable="true"
      style="min-width: 130px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutocompleteApi
            v-model:value="data.output.owner"
            v-model:valueId="data.output.ownerId"
            :search="fetchOwners"
          ></AutocompleteApi>
        </span>
        <span v-else>
          <span v-if="data.input.owner && !data.output.owner" class="invalid">
            {{ data.input.owner }}
          </span>
          <span v-else>
            <span v-if="data.output.owner?.id">
              <router-link
                :to="{
                  name: 'owner.edit',
                  params: { id: data.output.owner?.id },
                }"
                target="_blank"
                style="text-decoration: none"
                >{{ $sliceText(data.output.owner?.name, 15) }}</router-link
              >
            </span>
            <span v-else> - </span>
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Cor"
      field="ouput.color"
      :sortable="true"
      style="min-width: 120px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <InputText v-model="data.output.color" />
        </span>
        <span v-else-if="data.output.color">
          {{ data.output.color }}
        </span>
        <span v-else> - </span>
      </template>
    </Column>
    <Column
      v-for="i in serviceId ? 0 : 1"
      :key="i"
      header="Valor de Compra"
      field="output.buyValue"
      :sortable="true"
      style="min-width: 100px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <InputNumber
            v-model="data.output.buyValue"
            mode="currency"
            currency="BRL"
            locale="pt-BR"
          />
        </span>
        <span v-else>
          <span
            v-if="data.input.buyValue && !data.output.buyValue"
            class="invalid"
          >
            {{ data.input.buyValue }}
          </span>
          <span v-else>
            {{ data.output.buyValue ? $toDecimal(data.output.buyValue) : '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      v-for="i in serviceId ? 1 : 0"
      :key="i"
      header="Inseminação"
      field="output.serviceBullType.name"
      :sortable="true"
      style="min-width: 130px"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <AutoComplete
            v-model="data.output.serviceBullType"
            :suggestions="bullTypes"
            @complete="fetchBullTypes($event)"
            field="name"
            dropdown
          ></AutoComplete>
        </span>
        <span v-else>
          {{
            data.output.serviceBullType?.name ||
            data.output.serviceBullType ||
            '-'
          }}
        </span>
      </template>
    </Column>
    <Column
      v-for="i in serviceId ? 1 : 0"
      :key="i"
      header="Dt. Execução do Serviço"
      field="serviceExecution"
      style="min-width: 150px"
      :sortable="true"
    >
      <template #body="{ data }">
        <span v-if="data.editing">
          <Calendar
            v-model="data.output.serviceExecution"
            dateFormat="dd/mm/yy"
            showIcon
            :monthNavigator="true"
            :yearNavigator="true"
            :yearRange="birthDateRange"
            :showOnFocus="false"
            v-mask="'##/##/####'"
          />
        </span>
        <span v-else>
          <span
            v-if="data.input.serviceExecution && !data.output.serviceExecution"
            class="invalid"
          >
            {{ data.input.serviceExecution }}
          </span>
          <span v-else>
            {{ $formatDate(data.output.serviceExecution) || '-' }}
          </span>
        </span>
      </template>
    </Column>
    <Column
      header="Ações"
      alignFrozen="right"
      :frozen="true"
      style="min-width: 100px"
    >
      <template #body="props">
        <span v-if="!props.data.editing">
          <Button
            type="button"
            icon="pi pi-pencil"
            class="p-button-text p-button-secondary p-mr-1"
            @click="initEdit(props.data)"
          ></Button>
          <Button
            type="button"
            icon="pi pi-trash"
            class="p-button-text p-button-secondary"
            @click="deleteItem(props.index)"
          ></Button>
        </span>
        <span v-else>
          <Button
            type="button"
            icon="pi pi-check"
            class="p-button-text p-button-secondary p-mr-1"
            @click="saveEdit(props.data)"
          ></Button>
          <Button
            type="button"
            icon="pi pi-times"
            class="p-button-text p-button-secondary"
            @click="cancelEdit(props.data)"
          ></Button>
        </span>
      </template>
    </Column>
  </DataTable>
</template>

<script>
import { uid } from 'uid';
import AutocompleteApi from '../../../components/AutocompleteApi.vue';
import BovineType from '../../../components/BovineType.vue';
import { bovineRootTypes } from '../../../lib/bovines';
import {
  BovineApi,
  BreedApi,
  BullApi,
  FarmApi,
  FarmLotApi,
  OwnerApi,
} from '../../../services/api';

const getEditMulti = () => ({
  output: {
    type: null,
    typeId: null,
    breed: null,
    breedId: null,
    birthDate: null,
    childBirth: null,
    weight: null,
    status: null,
    statusId: null,
    bullType: null,
    farmLot: null,
    lotId: null,
    farm: null,
    farmId: null,
    owner: null,
    ownerId: null,
    buyValue: null,
    serviceBullType: null,
    serviceExecution: null,
  },
  input: {},
  editing: true,
  editMulti: true,
});

export default {
  name: 'BovineImportTable',
  components: {
    BovineType,
    AutocompleteApi,
  },
  props: {
    rows: Array,
    serviceId: Number,
    loading: Boolean,
  },
  data() {
    return {
      selection: {},
      selectionAll: false,
      editMulti: [getEditMulti()],
      bullTypes: [],
      birthDateRange: `${this.$dayjs()
        .subtract(10, 'y')
        .format('YYYY')}:${this.$dayjs().add(10, 'y').format('YYYY')}`,
    };
  },
  created() {
    this.formatRows();
  },
  methods: {
    addEmptyItem() {
      this.$emit('update:rows', [
        {
          output: {
            typeId: null,
            earring: null,
            farmId: null,
          },
          input: {},
          editing: true,
          id: uid(),
        },
        ...this.rows,
      ]);
    },
    deleteItem(index) {
      const rows = this.rows.filter((row, i) => i !== index);
      this.$emit('update:rows', rows);
    },
    initEdit(data) {
      data.oldOutput = JSON.parse(JSON.stringify(data.output));
      data.editing = true;
    },
    cancelEdit(record) {
      if (record.editMulti) {
        this.resetEditMulti(record);
        return;
      }
      record.output = record.oldOutput;
      record.oldOutput = null;
      record.editing = false;
    },
    async saveEdit(record) {
      if (record.editMulti) {
        this.applyEditMulti(record);
        return;
      }
      await this.checkId(record);
      record.oldOutput = null;
      record.editing = false;
      this.$emit('update:rows', [...this.rows]);
    },
    fetchTypes: (query) => BovineApi.fetchTypes(query),
    fetchBreeds: (query) => BreedApi.fetch(query),
    fetchStatus: (query) => BovineApi.fetchStatus(query),
    fetchFarmLots: (query) => FarmLotApi.fetch(query),
    fetchFarms: (query) => FarmApi.fetch(query),
    fetchOwners: (query) => OwnerApi.fetch(query),
    async fetchBullTypes(event) {
      const result = await BullApi.fetch({
        name: event.query,
        orderField: 'name',
        order: 'asc',
      });
      this.bullTypes = result.results;
    },
    fetchBovineRoot: (bovine, query) =>
      BovineApi.fetch({
        ...query,
        typeId: bovineRootTypes,
        orderField: 'earring',
        order: 'asc',
        available: true,
        include: '[farmLot]',
        includeFields: '[id,earring]',
        page: 'all',
        farmId: bovine.farmId,
      }),
    formatRows() {
      if (!this.rows.length) {
        return;
      }
      const rows = this.rows.map((row) => ({ ...row, id: uid() }));
      this.$emit('update:rows', rows);
    },
    resetEditMulti(record) {
      record.editing = false;
      this.$nextTick(() => {
        const editMulti = getEditMulti();
        record.output = editMulti.output;
        record.editing = true;
      });
    },
    async applyEditMulti(record) {
      let checkIds = false;

      const updatedRows = this.rows.map((row) => {
        if (!this.selection[row.id] && !this.selectionAll) {
          return row;
        }
        Object.keys(record.output).forEach((key) => {
          if (record.output[key] === null) {
            return;
          }
          row.output[key] = record.output[key];

          if (!checkIds && ['earring', 'farmId', 'typeId'].includes(key)) {
            checkIds = true;
          }
        });
        return row;
      });

      if (checkIds) {
        await this.checkAllIds(updatedRows);
      }

      this.$emit('update:rows', updatedRows);
      this.resetEditMulti(record);
    },
    onChangeSelection(record) {
      const value = !this.selection[record.id];
      this.selection[record.id] = value;
      if (!value) {
        this.selectionAll = false;
      }
    },
    onChangeSelectionAll() {
      const value = !this.selectionAll;
      this.selectionAll = value;
      if (!value) {
        this.selection = {};
      } else {
        this.rows.forEach((row) => {
          this.selection[row.id] = true;
        });
      }
    },
    onStatusChange(data, event) {
      if (event?.id === 2) {
        data.output.childBirth = this.$dayjs().add(9, 'month').toDate();
      } else {
        data.output.childBirth = null;
      }
    },
    async checkId(record) {
      try {
        this.$emit('update:loading', true);
        const [result] = await BovineApi.findIds([
          {
            earring: record.output.earring,
            farmId: record.output.farmId,
            typeId: record.output.typeId,
          },
        ]);
        record.output.id = result.id;
      } finally {
        this.$emit('update:loading', false);
      }
    },
    async checkAllIds(rows) {
      try {
        this.$emit('update:loading', true);
        const params = rows.map((row) => ({
          earring: row.output.earring,
          farmId: row.output.farmId,
          typeId: row.output.typeId,
        }));
        const results = await BovineApi.findIds(params);
        rows.forEach((row, index) => {
          row.output.id = results?.[index]?.id || null;
        });
      } finally {
        this.$emit('update:loading', false);
      }
    },
  },
};
</script>

<style scoped>
.invalid {
  color: #f44336;
}
div >>> .p-inputtext {
  width: 100%;
}

div >>> table tbody:first-of-type tr td {
  background: #f8f9fa;
}

div >>> table tbody:first-of-type tr .p-checkbox {
  visibility: hidden;
}

div >>> table tbody:first-of-type {
  z-index: 3;
}

div >>> .p-datatable-thead {
  z-index: 3;
}

div >>> table tbody tr td {
  z-index: 1;
}

div >>> .p-frozen-column {
  z-index: 2;
}
</style>
